/*
 * The one rule set for URLs that arrive from an untrusted source (Google
 * payloads, the wire) and end up in an `href`/`src`. The server sanitizes with
 * it (`@voll-intelligence/google-maps`) and the browser re-validates the wire
 * with it (`genui-kit` place schemas): two copies of these rules had drifted,
 * and a URL the server kept but the browser refused failed the whole parse.
 *
 * Each function returns the normalized URL, or `undefined` when the value is
 * not a string, does not parse, carries credentials, or falls outside the
 * accepted protocol/host.
 */

function parsedWebUrl(raw: unknown, httpsOnly: boolean): URL | undefined {
  if (typeof raw !== 'string' || raw.length === 0) return undefined;
  try {
    const url = new URL(raw);
    const acceptedProtocol = httpsOnly
      ? url.protocol === 'https:'
      : url.protocol === 'https:' || url.protocol === 'http:';
    if (!acceptedProtocol || url.username.length > 0 || url.password.length > 0) return undefined;
    return url;
  } catch {
    return undefined;
  }
}

/** `URL` already lowercases the hostname, so a plain suffix check is enough. */
function onHost(url: URL, domain: string): boolean {
  return url.hostname === domain || url.hostname.endsWith(`.${domain}`);
}

/** Any http(s) page without credentials — a place's own website. */
export function safePublicWebUrl(raw: unknown): string | undefined {
  return parsedWebUrl(raw, false)?.toString();
}

/** An https link to Google itself (Maps page, author profile). */
export function safeGoogleSourceUrl(raw: unknown): string | undefined {
  const url = parsedWebUrl(raw, true);
  return url && onHost(url, 'google.com') ? url.toString() : undefined;
}

/**
 * An https image on Google's content host. A `key` parameter (any case) is
 * refused: an image URL is rendered in the browser, and a key in it would leak
 * the Maps API key.
 */
export function safeGoogleImageUrl(raw: unknown): string | undefined {
  const url = parsedWebUrl(raw, true);
  if (!url || !onHost(url, 'googleusercontent.com')) return undefined;
  const hasKey = [...url.searchParams.keys()].some((key) => key.toLowerCase() === 'key');
  return hasKey ? undefined : url.toString();
}

/** As telas de busca que um link de cotação pode reabrir, pelo host da VOLL. */
const VOLL_SEARCH_PATH = /^\/intelligence\/(?:flight|hotel|rental|bus)\/booking$/;

/**
 * O link "refazer esta busca" de uma cotação em PDF (#5156): https, no domínio da
 * VOLL e numa das quatro telas de busca. O link chega do navegador, e o PDF sai com
 * a marca da VOLL — qualquer outro destino seria a VOLL assinando um link de terceiro.
 */
export function safeVollSearchUrl(raw: unknown): string | undefined {
  const url = parsedWebUrl(raw, true);
  if (!url || !onHost(url, 'govoll.com') || url.port !== '') return undefined;
  return VOLL_SEARCH_PATH.test(url.pathname) ? url.toString() : undefined;
}
